/**
 * Озвучка українською через Google Translate TTS.
 *
 * Чому так, а не window.speechSynthesis:
 *  • системні голоси нестабільні й часто взагалі не українські;
 *  • Google TTS дає однакову якість у всіх браузерах;
 *  • прямий тег <audio> може відтворювати крос-доменний звук без CORS.
 *
 * Важливі правила:
 *  • перед кожним запуском зупиняємо попередній звук;
 *  • кожен виклик має власну сесію, щоб запізнілі події не ламали стан кнопки;
 *  • довгий текст ріжемо на короткі запити: translate_tts не любить великі q.
 */

export const TTS_LANG = 'uk'
export const TTS_CLIENT = 'tw-ob'
export const MAX_TTS_CHARS = 180

const COMBINING_ACUTE = '́'
const STRESS_LEADING = 'ˈ'
const UKRAINIAN_VOWELS = 'аеєиіїоуюяАЕЄИІЇОУЮЯ'

const isVowel = (char) => UKRAINIAN_VOWELS.includes(char)

/** Прибирає Markdown/KaTeX/HTML, щоб TTS не читав символи форматування. */
export function stripTtsMarkup(text) {
  return String(text ?? '')
    .replace(/!\[([^\]]*)\]\([^)]*\)/g, '')
    .replace(/\[([^\]]+)\]\([^)]*\)/g, '$1')
    .replace(/(`{1,3}|~{2}|\*{1,3}|_{1,3})/g, ' ')
    .replace(/\\([()[\]])/g, ' ')
    .replace(/\$+/g, ' ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/^[#>]+/gm, ' ')
    .replace(/\|/g, ' ')
    .replace(/\s+([.,:;!?])/g, '$1')
    .replace(/\s+/g, ' ')
    .trim()
}

/**
 * Готує текст для Google TTS.
 *
 * U+0301 і ˈ не відправляємо як є: TTS їх або ігнорує, або читає як шум.
 * Натомість робимо ударну голосну великою: «чита́ти» → «читАти».
 * Markdown/KaTeX теж не читаємо вголос: залишаємо лише людський текст.
 */
export function prepareTtsText(text) {
  const chars = Array.from(stripTtsMarkup(text))
  let out = ''

  for (let i = 0; i < chars.length; i += 1) {
    const char = chars[i]

    if (char === COMBINING_ACUTE) {
      const letters = Array.from(out)
      for (let j = letters.length - 1; j >= 0; j -= 1) {
        if (isVowel(letters[j])) {
          letters[j] = letters[j].toUpperCase()
          break
        }
      }
      out = letters.join('')
      continue
    }

    if (char === STRESS_LEADING) {
      const next = chars[i + 1]
      if (next && /[\p{L}]/u.test(next)) {
        out += next.toUpperCase()
        i += 1
      }
      continue
    }

    out += char
  }

  return out.replace(/\s+/g, ' ').trim()
}

/**
 * Ріже довгий текст на запити, які translate_tts проковтне.
 * Ріжемо по реченнях, а всередині довгого речення — по словах.
 */
export function splitTtsText(text, maxChars = MAX_TTS_CHARS) {
  const clean = prepareTtsText(text)
  if (!clean) return []
  if (clean.length <= maxChars) return [clean]

  const sentences = clean
    .split(/(?<=[.!?…])\s+/u)
    .map((sentence) => sentence.trim())
    .filter(Boolean)
  const chunks = []
  let current = ''

  const push = (chunk) => {
    const value = chunk.trim()
    if (value) chunks.push(value)
  }

  for (const sentence of sentences.length ? sentences : [clean]) {
    if (sentence.length <= maxChars) {
      if ((`${current} ${sentence}`.trim().length <= maxChars)) {
        current = `${current} ${sentence}`.trim()
      } else {
        push(current)
        current = sentence
      }
      continue
    }

    push(current)
    current = ''
    for (const word of sentence.split(/\s+/)) {
      if ((`${current} ${word}`.trim().length <= maxChars)) {
        current = `${current} ${word}`.trim()
      } else {
        push(current)
        current = word
      }
    }
  }
  push(current)

  return chunks.flatMap((chunk) => {
    if (chunk.length <= maxChars) return [chunk]
    const parts = []
    for (let i = 0; i < chunk.length; i += maxChars) {
      parts.push(chunk.slice(i, i + maxChars))
    }
    return parts
  })
}

/** Прямий URL Google Translate TTS для української озвучки. */
export function ttsUrl(text) {
  const chunk = String(text ?? '').trim()
  return `https://translate.google.com/translate_tts?ie=UTF-8&tl=${TTS_LANG}&client=${TTS_CLIENT}&q=${encodeURIComponent(chunk)}`
}

export const isSupported = () =>
  typeof window !== 'undefined' && typeof window.Audio === 'function'

let sessionId = 0
let currentAudio = null

function teardown(audio) {
  if (!audio) return
  try {
    audio.pause()
  } catch {
    /* ігноруємо */
  }
  try {
    audio.removeAttribute('src')
    audio.load()
  } catch {
    /* у деяких браузерах load без src кидає — не критично */
  }
}

/** Зупиняє поточне відтворення і робить усі запізнілі події неважливими. */
export function stop() {
  sessionId += 1
  const audio = currentAudio
  currentAudio = null
  teardown(audio)
}

function playChunk(url, { onStart } = {}) {
  const session = sessionId

  return new Promise((resolve, reject) => {
    const audio = new window.Audio()
    currentAudio = audio
    const stale = () => session !== sessionId || currentAudio !== audio

    const cleanup = () => {
      if (currentAudio === audio) currentAudio = null
      audio.removeEventListener('playing', handlePlaying)
      audio.removeEventListener('ended', handleEnded)
      audio.removeEventListener('error', handleError)
    }

    const handlePlaying = () => {
      if (stale()) return
      onStart?.()
    }
    const handleEnded = () => {
      if (stale()) {
        cleanup()
        resolve(false)
        return
      }
      cleanup()
      resolve(true)
    }
    const handleError = () => {
      if (stale()) {
        cleanup()
        resolve(false)
        return
      }
      cleanup()
      reject(new Error('Не вдалося завантажити або відтворити аудіо Google TTS'))
    }

    audio.addEventListener('playing', handlePlaying)
    audio.addEventListener('ended', handleEnded)
    audio.addEventListener('error', handleError)
    audio.preload = 'auto'
    audio.src = url
    try {
      audio.load()
    } catch {
      /* якщо load недоступний — play все одно спробує */
    }

    const started = audio.play()
    started?.catch?.((err) => {
      if (stale()) {
        cleanup()
        resolve(false)
        return
      }
      cleanup()
      reject(err instanceof Error ? err : new Error('Браузер заблокував відтворення аудіо'))
    })
  })
}

/**
 * Відтворює текст українським голосом Google.
 * Повертає true, якщо звук дограв до кінця, і false, якщо його зупинили.
 * Помилки віддаємо через onError і не кидаємо назовні, щоб клік не падав.
 */
export async function playAudio(text, opts = {}) {
  stop()
  if (!isSupported()) {
    opts.onError?.(new Error('Браузер не підтримує відтворення аудіо'))
    return false
  }

  const chunks = splitTtsText(text)
  if (!chunks.length) {
    opts.onError?.(new Error('Немає тексту для озвучки'))
    return false
  }

  const session = sessionId
  let started = false

  try {
    for (const chunk of chunks) {
      if (session !== sessionId) return false
      const finished = await playChunk(ttsUrl(chunk), {
        onStart: () => {
          if (!started && session === sessionId) {
            started = true
            opts.onStart?.()
          }
        },
      })
      if (!finished || session !== sessionId) return false
    }
    if (session === sessionId) opts.onEnd?.()
    return session === sessionId
  } catch (err) {
    if (session === sessionId) opts.onError?.(err)
    return false
  }
}

/** Сумісний вхід для кнопок: та сама сигнатура, що була у старого speak. */
export function speak(text, opts = {}) {
  void playAudio(text, opts)
  return true
}

/** Озвучує слова по черзі — зручно для наголосів. Повертає функцію зупинки. */
export function speakSequence(items, opts = {}) {
  const chunks = (Array.isArray(items) ? items : [items])
    .map((item) => prepareTtsText(item))
    .filter(Boolean)
  if (!isSupported() || !chunks.length) {
    opts.onError?.(new Error('Немає тексту для озвучки'))
    return () => {}
  }

  stop()
  const session = sessionId
  let started = false

  void (async () => {
    try {
      for (const chunk of splitTtsText(chunks.join(' '))) {
        if (session !== sessionId) return
        const finished = await playChunk(ttsUrl(chunk), {
          onStart: () => {
            if (!started && session === sessionId) {
              started = true
              opts.onStart?.()
            }
          },
        })
        if (!finished || session !== sessionId) return
      }
      if (session === sessionId) opts.onEnd?.()
    } catch (err) {
      if (session === sessionId) opts.onError?.(err)
    }
  })()

  return () => stop()
}
